import { styled } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

import { NEW_CASE_STATUSES } from '#entities';
import {
  sectionClasses,
  NewCaseCards,
  type NewCaseCardsProps,
  type WidgetConfigBase,
  type WidgetProps,
} from '#features';

import { Widget } from '../base';

const PREFIX = 'TSMED-WidgetNewCases';

const Root = styled(Widget, {
  name: PREFIX,
  overridesResolver: (props, styles) => styles.root,
})({
  overflow: 'visible',
  height: '100%',
});

// Omit props that will be provided by the WidgetNewCases component itself
type OmitProps = 'maxHeight' | 'availableHeight' | 'instance' | 'side';

const config: WidgetConfigBase<Omit<NewCaseCardsProps, OmitProps>> = {
  caseStatuses: {
    type: 'select',
    label: 'Case Statuses',
    options: NEW_CASE_STATUSES.map(status => ({
      label: status,
      value: status,
    })),
    multiple: true,
    defaultValue: [],
  },
  sortBy: {
    type: 'select',
    label: 'Sort By',
    options: [
      { label: 'ID', value: 'id' },
      { label: 'Donor Type', value: 'donorType' },
      { label: 'Label', value: 'label' },
      { label: 'OSC Status', value: 'oscStatus' },
      { label: 'OSC Status Sequence', value: 'oscStatusSequence' },
      { label: 'Donor/Time String', value: 'donorORTimeString' },
    ],
    defaultValue: 'oscStatusSequence',
  },
  maxRows: {
    type: 'number',
    label: 'Max Rows',
    defaultValue: 2,
    min: 0,
  },
  highlightDuration: {
    type: 'number',
    label: 'Highlight Fade Out Duration (seconds)',
    defaultValue: 5,
    min: 0,
  },
  variant: {
    type: 'select',
    label: 'Variant',
    hidden: true,
    options: [
      { label: 'Horizontal', value: 'horizontal' },
      { label: 'Vertical', value: 'vertical' },
    ],
    defaultValue: 'horizontal',
  },
};

type Settings = Omit<NewCaseCardsProps, OmitProps>;

const getSide = (
  variant: Settings['variant'],
  instanceIndex: number | null | undefined,
): NewCaseCardsProps['side'] => {
  if (variant !== 'vertical') {
    return undefined;
  }
  // instanceIndex 0 = left aside, 1 = right aside
  if (instanceIndex === 0) {
    return 'left';
  }
  if (instanceIndex === 1) {
    return 'right';
  }
  return undefined;
};

/**
 * WidgetNewCases component for displaying new case alerts with widget settings.
 * Follows the project's card design patterns and performance guidelines.
 *
 * **Variant Auto-Detection:**
 * - When used as **aside** (with instanceIndex) → automatically uses **'vertical'** layout
 * - When used as **standalone** (no instanceIndex) → automatically uses **'horizontal'** layout
 *
 * This ensures proper rendering regardless of where the widget is placed.
 */
export const WidgetNewCases = (props: WidgetProps<Settings>) => {
  const { showSettingsButton = true, instance, className, style } = props;

  const ref = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState<number>();

  useEffect(() => {
    const parent = ref.current?.closest(`.${sectionClasses.body}`);
    if (!parent) {
      return;
    }
    const observer = new ResizeObserver(() =>
      setMaxHeight(parent.clientHeight),
    );
    observer.observe(parent);
    return () => observer.disconnect();
  }, []);

  // Disable settings button when widget is placed in aside position
  const isAsideSection = typeof instance?.instanceIndex === 'number';
  const shouldRenderSettings = showSettingsButton && !isAsideSection;

  return (
    <Root
      ref={ref}
      instance={instance}
      config={config}
      showSettingsButton={shouldRenderSettings}
      className={className}
      style={style}
    >
      {(resolved: Settings) => {
        // Auto-detect variant based on aside position:
        // - If widget has instanceIndex (used as aside) → force 'vertical'
        // - If widget is standalone (no instanceIndex) → force 'horizontal'
        const autoVariant: Settings['variant'] = isAsideSection
          ? 'vertical'
          : 'horizontal';

        return (
          <NewCaseCards
            {...resolved}
            variant={autoVariant}
            maxHeight={maxHeight}
            instance={instance}
            side={getSide(autoVariant, instance?.instanceIndex)}
          />
        );
      }}
    </Root>
  );
};

WidgetNewCases.config = config;
